import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import { FormControl, FormHelperText, InputLabel, MenuItem, Select, TextField } from '@mui/material';
import DashboardLayout from 'examples/LayoutContainers/DashboardLayout';
import DashboardNavbar from 'examples/Navbars/DashboardNavbar';
import Footer from "examples/Footer";
import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import MDButton from 'components/MDButton';
import axios from 'axios';

export default function EditDefaultRoles() {
    const { id } = useParams();
    const apiUrl = process.env.REACT_APP_API_BASE_URL;
    const bearerToken = localStorage.getItem('token');
    const navigate = useNavigate();
    const [isLoading, setIsLoading] = useState(false);
    const [permissions, setPermissions] = useState([]);

    const editRole = useFormik({
        initialValues: {
            name: '',
            permissions: [],
        },
        validationSchema: Yup.object({
            name: Yup.string().required('Required'),
        }),
        onSubmit: async (values) => {
            setIsLoading(true);
            try {
                const response = await axios.put(
                    `${apiUrl}auth-service/default-role/${id}`,
                    values,
                    {
                        headers: { Authorization: `Bearer ${bearerToken}` },
                    }
                );
                if (response.data?.success) {
                    toast.info(response.data?.message || 'Role updated successfully!');
                    navigate(-1);
                } else {
                    throw new Error('Failed to update role');
                }
            } catch (error) {
                toast.error(error.response?.data?.message || error.message);
            } finally {
                setIsLoading(false);
            }
        },
    });

    useEffect(() => {
        const fetchData = async () => {
            if (!bearerToken) return;

            try {
                const permissionsResponse = await axios.get(`${apiUrl}auth-service/permission`, {
                    headers: { Authorization: `Bearer ${bearerToken}` },
                });
                if (permissionsResponse.data?.success) {
                    setPermissions(permissionsResponse.data.data);
                } else {
                    throw new Error('Failed to fetch permissions');
                }

                const roleResponse = await axios.get(`${apiUrl}auth-service/default-role/${id}`, {
                    headers: { Authorization: `Bearer ${bearerToken}` },
                });
                if (roleResponse.data?.success) {
                    const roleData = roleResponse.data.data;
                    editRole.setValues({
                        name: roleData.name,
                        permissions: roleData.defaultPermissions.map((p) => p.id),
                    });
                } else {
                    throw new Error('Failed to fetch role details');
                }
            } catch (error) {
                toast.error(error.response?.data?.message || error.message);
            }
        };

        fetchData();
    }, [id, bearerToken]);

    return (
        <DashboardLayout>
            <DashboardNavbar />
            <MDBox pt={6} pb={3}>
                <Grid container spacing={6}>
                    <Grid item xs={12}>
                        <form onSubmit={editRole.handleSubmit}>
                            <Card>
                                <MDBox
                                    mx={2}
                                    mt={-3}
                                    py={3}
                                    px={2}
                                    variant="gradient"
                                    bgColor="info"
                                    borderRadius="lg"
                                    coloredShadow="info"
                                    display="flex"
                                    justifyContent="space-between"
                                >
                                    <MDTypography variant="h6" color="white">
                                        Edit Default Role
                                    </MDTypography>
                                </MDBox>
                                <MDBox p={2}>
                                    <MDBox p={1}>
                                        <TextField
                                            fullWidth
                                            variant="outlined"
                                            id="name"
                                            label="Role Name"
                                            name="name"
                                            value={editRole.values.name}
                                            onChange={editRole.handleChange}
                                            onBlur={editRole.handleBlur}
                                            error={editRole.touched.name && Boolean(editRole.errors.name)}
                                            helperText={editRole.touched.name && editRole.errors.name}
                                        />
                                    </MDBox>
                                    <MDBox p={1}>
                                        <FormControl fullWidth error={Boolean(editRole.touched.permissions && editRole.errors.permissions)}>
                                            <InputLabel>Select Permissions</InputLabel>
                                            <Select
                                                multiple
                                                value={editRole.values.permissions}
                                                onChange={(e) => {
                                                    const selectedValues = e.target.value;
                                                    editRole.setFieldValue('permissions', selectedValues);
                                                }}
                                                sx={{ height: '45px' }}
                                                onBlur={editRole.handleBlur}
                                                renderValue={(selected) =>
                                                    selected
                                                        .map((id) => permissions.find((p) => p.id === id)?.name)
                                                        .filter(Boolean)
                                                        .join(', ')
                                                }
                                            >
                                                {permissions.map((permission) => (
                                                    <MenuItem key={permission.id} value={permission.id}>
                                                        {permission.name}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                            {editRole.touched.permissions && editRole.errors.permissions && (
                                                <FormHelperText>{editRole.errors.permissions}</FormHelperText>
                                            )}
                                        </FormControl>
                                    </MDBox>
                                    <MDBox p={1} display="flex" alignItems="center">
                                        <MDButton color="info" type="submit" disabled={isLoading}>
                                            {isLoading ? 'Updating...' : 'Update'}
                                        </MDButton>
                                    </MDBox>
                                </MDBox>
                            </Card>
                        </form>
                    </Grid>
                </Grid>
            </MDBox>
            <Footer />
            <ToastContainer
                position="bottom-right"
                autoClose={5000}
                hideProgressBar={false}
                newestOnTop={false}
                closeOnClick
                rtl={false}
                pauseOnFocusLoss
                draggable
                pauseOnHover
                theme="light"
            />
        </DashboardLayout>
    );
}
